import { useMemo, useState, useCallback } from "react";
import logo from "@/assets/logo.png";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { Users, BarChart3, Zap, Mail, ArrowRight, Heart, MessageSquare, Globe, Send } from "lucide-react";
import DisclaimerDialog from "@/components/DisclaimerDialog";
import { useI18n } from "@/hooks/use-i18n";
import { supabase } from "@/integrations/supabase/client";
import type { TranslationKey } from "@/lib/i18n";

const SLOGAN_KEYS: TranslationKey[] = [
  "landingSlogan1",
  "landingSlogan2",
  "landingSlogan3",
  "landingSlogan4",
  "landingSlogan5",
  "landingSlogan6",
] as const;

const HomePage = () => {
  const navigate = useNavigate();
  const { t, lang, setLang } = useI18n();
  const sloganKey = useMemo(() => SLOGAN_KEYS[Math.floor(Math.random() * SLOGAN_KEYS.length)], []);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackError, setFeedbackError] = useState(false);

  const handleGetStarted = () => {
    const alreadyAccepted = localStorage.getItem("partyflow_disclaimer_accepted");
    if (alreadyAccepted) {
      navigate("/app?mode=signup");
    } else {
      setShowDisclaimer(true);
    }
  };

  const handleFeedback = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    if (formData.get("website")) return;

    const name = (formData.get("name") as string)?.slice(0, 100) || null;
    const email = (formData.get("email") as string)?.slice(0, 255) || null;
    const message = (formData.get("message") as string)?.slice(0, 2000) || null;

    if (!name && !email && !message) return;

    setFeedbackSending(true);
    setFeedbackError(false);

    const feedbackId = crypto.randomUUID();
    const { error } = await supabase.from("feedback").insert({ id: feedbackId, name, email, message });

    if (error) {
      setFeedbackSending(false);
      setFeedbackError(true);
      return;
    }

    // Send admin notification via SMTP
    supabase.functions.invoke("send-smtp-email", {
      body: {
        to: "contact@partyflow.ch",
        subject: `Nouveau feedback${name ? ` de ${name}` : ""}`,
        html: `<div style="max-width:560px;margin:0 auto;font-family:'Inter',Arial,sans-serif">
  <div style="background:#0a0a0a;padding:24px 25px;border-radius:8px 8px 0 0">
    <h1 style="font-size:20px;font-weight:bold;color:#e6c200;margin:0">🎉 PartyFlow</h1>
  </div>
  <div style="background:#141414;padding:32px 25px">
    <h2 style="font-size:22px;font-weight:bold;color:#ece9dd;margin:0 0 16px">Nouveau feedback</h2>
    <hr style="border-color:#242424;margin:0 0 24px"/>
    <p style="font-size:11px;color:#666;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 4px">Nom</p>
    <p style="font-size:15px;color:#ece9dd;line-height:1.5;margin:0 0 16px">${name || "—"}</p>
    <p style="font-size:11px;color:#666;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 4px">Email</p>
    <p style="font-size:15px;color:#ece9dd;line-height:1.5;margin:0 0 16px">${email || "—"}</p>
    <hr style="border-color:#242424;margin:16px 0"/>
    <p style="font-size:11px;color:#666;text-transform:uppercase;letter-spacing:0.5px;margin:0 0 4px">Message</p>
    <p style="font-size:15px;color:#ece9dd;line-height:1.6;margin:0;background:#1e1e1e;padding:16px;border-radius:6px;border-left:3px solid #e6c200">${message || "—"}</p>
  </div>
  <div style="background:#0a0a0a;padding:20px 25px;border-radius:0 0 8px 8px">
    <p style="font-size:13px;color:#666;margin:0">Notification admin – PartyFlow</p>
  </div>
</div>`,
      },
    });

    // Send confirmation to user if they provided an email
    if (email) {
      supabase.functions.invoke("send-smtp-email", {
        body: {
          to: email,
          subject: "Merci pour votre message – PartyFlow",
          html: `<div style="max-width:560px;margin:0 auto;font-family:'Inter',Arial,sans-serif">
  <div style="background:#0a0a0a;padding:24px 25px;border-radius:8px 8px 0 0">
    <h1 style="font-size:20px;font-weight:bold;color:#e6c200;margin:0">🎉 PartyFlow</h1>
  </div>
  <div style="background:#141414;padding:32px 25px">
    <h2 style="font-size:22px;font-weight:bold;color:#ece9dd;margin:0 0 16px">${name ? `Merci, ${name} !` : "Merci pour votre message !"}</h2>
    <hr style="border-color:#242424;margin:0 0 24px"/>
    <p style="font-size:15px;color:#a0a0a0;line-height:1.6;margin:0 0 16px">Nous avons bien reçu votre remarque et nous vous en remercions. Votre retour nous aide à améliorer PartyFlow.</p>
    <p style="font-size:15px;color:#a0a0a0;line-height:1.6;margin:0 0 16px">Si nécessaire, nous reviendrons vers vous dans les meilleurs délais.</p>
  </div>
  <div style="background:#0a0a0a;padding:20px 25px;border-radius:0 0 8px 8px">
    <p style="font-size:13px;color:#666;margin:0">L'équipe PartyFlow</p>
  </div>
</div>`,
          reply_to: "contact@partyflow.ch",
        },
      });
    }

    setFeedbackSending(false);
    setFeedbackSent(true);
    form.reset();
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />

      {/* Hero */}
      <section className="relative overflow-hidden py-24">
        {/* Gradient orbs */}
        <div className="pointer-events-none absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-[hsl(var(--color-pink)/0.15)] blur-3xl" />
        <div className="pointer-events-none absolute -top-20 right-0 h-[400px] w-[400px] rounded-full bg-[hsl(var(--color-yellow)/0.1)] blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/3 h-[300px] w-[300px] rounded-full bg-[hsl(var(--color-green)/0.1)] blur-3xl" />

        <div className="relative mx-auto max-w-5xl px-6 text-center">
          <div className="mx-auto max-w-2xl">
            <h2 className="font-display text-5xl font-extrabold leading-tight tracking-tight sm:text-6xl bg-gradient-to-r from-[hsl(var(--color-yellow))] via-[hsl(var(--color-pink))] to-[hsl(var(--color-green))] bg-clip-text text-transparent">
              {t("landingHero")}
            </h2>
            <div className="mt-8 rounded-xl border-2 border-[hsl(var(--color-yellow)/0.4)] bg-[hsl(var(--color-yellow)/0.1)] px-6 py-4">
              <p className="text-lg font-medium text-[hsl(var(--color-yellow))] leading-relaxed italic">"{t(sloganKey)}"</p>
            </div>
            <Button
              size="lg"
              className="mt-8 text-base px-8 bg-gradient-to-r from-[hsl(var(--color-yellow))] via-[hsl(var(--color-pink))] to-[hsl(var(--color-green))] text-background font-bold hover:opacity-90 transition-opacity"
              onClick={handleGetStarted}
            >
              {t("landingGetStarted")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <DisclaimerDialog open={showDisclaimer} onOpenChange={setShowDisclaimer} />

      {/* What is it for */}
      <section className="border-t border-border py-20">
        <div className="mx-auto max-w-5xl px-6">
          <h3 className="text-center font-display text-3xl font-bold text-[hsl(var(--color-yellow))]">
            {t("landingWhatTitle")}
          </h3>
          <p className="mx-auto mt-4 max-w-2xl text-center text-muted-foreground">
            {t("landingWhatDesc")}
          </p>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {[
              {
                icon: Users,
                title: t("landingFeat1Title"),
                desc: t("landingFeat1Desc"),
                color: "var(--color-yellow)",
              },
              {
                icon: BarChart3,
                title: t("landingFeat2Title"),
                desc: t("landingFeat2Desc"),
                color: "var(--color-pink)",
              },
              {
                icon: Zap,
                title: t("landingFeat3Title"),
                desc: t("landingFeat3Desc"),
                color: "var(--color-green)",
              },
            ].map((f, i) => (
              <Card
                key={i}
                className="border-border bg-card hover:border-[hsl(var(--color-pink)/0.4)] transition-colors"
              >
                <CardContent className="flex flex-col items-center p-6 text-center">
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `hsl(${f.color} / 0.15)`, color: `hsl(${f.color})` }}
                  >
                    <f.icon className="h-6 w-6" />
                  </div>
                  <h4 className="mt-4 font-semibold text-foreground">{f.title}</h4>
                  <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How does it work */}
      <section className="border-t border-border py-20">
        <div className="mx-auto max-w-5xl px-6">
          <h3 className="text-center font-display text-3xl font-bold text-[hsl(var(--color-pink))]">
            {t("landingHowTitle")}
          </h3>
          <div className="mx-auto mt-10 max-w-md space-y-6">
            {[
              t("landingHow1"),
              t("landingHow2"),
              t("landingHow3"),
              t("landingHow4"),
            ].map((step: string, i: number) => {
              const colors = ["var(--color-yellow)", "var(--color-pink)", "var(--color-green)", "var(--color-yellow)"];
              return (
                <div key={i} className="flex items-start gap-4">
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-background"
                    style={{ backgroundColor: `hsl(${colors[i]})` }}
                  >
                    {i + 1}
                  </span>
                  <p className="pt-1 text-muted-foreground">{step}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Free */}
      <section className="border-t border-border py-20">
        <div className="mx-auto max-w-5xl px-6 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--color-green)/0.15)] text-[hsl(var(--color-green))]">
            <Heart className="h-7 w-7" />
          </div>
          <h3 className="mt-6 font-display text-3xl font-bold text-[hsl(var(--color-green))]">{t("landingFreeTitle")}</h3>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            {t("landingFreeDesc")}
          </p>
        </div>
      </section>

      {/* Feedback with form */}
      <section className="border-t border-border py-20">
        <div className="mx-auto max-w-5xl px-6 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--color-yellow)/0.15)] text-[hsl(var(--color-yellow))]">
            <MessageSquare className="h-7 w-7" />
          </div>
          <h3 className="mt-6 font-display text-3xl font-bold text-[hsl(var(--color-yellow))]">{t("landingFeedbackTitle")}</h3>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            {t("landingFeedbackDesc")}
          </p>

          {feedbackSent ? (
            <p className="mt-8 text-[hsl(var(--color-green))] font-medium">{t("feedbackSuccess")}</p>
          ) : (
            <form onSubmit={handleFeedback} className="mx-auto mt-8 max-w-md space-y-4 text-left">
              {/* Honeypot - hidden from humans */}
              <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" />

              <Input
                name="name"
                placeholder={t("feedbackName")}
                maxLength={100}
              />
              <Input
                name="email"
                type="email"
                placeholder={t("feedbackEmail")}
                maxLength={255}
              />
              <Textarea
                name="message"
                placeholder={t("feedbackMessage")}
                maxLength={2000}
                rows={4}
              />
              {feedbackError && (
                <p className="text-sm text-destructive">{t("feedbackError")}</p>
              )}
              <Button
                type="submit"
                disabled={feedbackSending}
                className="w-full bg-[hsl(var(--color-yellow))] text-background font-bold hover:opacity-90 transition-opacity"
              >
                <Send className="mr-2 h-4 w-4" />
                {t("feedbackSend")}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="mx-auto max-w-5xl px-6 text-center text-sm text-muted-foreground space-y-2">
          <div>© {new Date().getFullYear()} PartyFlow.ch — {t("landingFooter")}</div>
          <a
            href="mailto:contact@partyflow.app"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-[hsl(var(--color-pink))] transition-colors"
          >
            <Mail className="h-3 w-3" />
            contact@partyflow.app
          </a>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
